import { useMemo } from 'react';
import { matchesSearchQuery, normalizeSearchQuery } from '@/shared/lib';
import { useWalletsAssetRows } from '@/widgets/wallet-assets';
import {
  useTradingAssetRows,
  type TradingAccountType,
  type TradingAssetRow,
} from '@/widgets/trading-assets';
import type { MarketAsset } from '@/widgets/market-assets';
import { useMarketsSearchAssets } from './useMarketsSearchAssets';

export type TradingSearchGroup = {
  type: TradingAccountType;
  rows: Array<TradingAssetRow>;
};

const useGlobalSearchResults = (query: string) => {
  const normalizedQuery = normalizeSearchQuery(query);

  const wallets = useWalletsAssetRows();
  const spot = useTradingAssetRows('spot');
  const leverage = useTradingAssetRows('leverage');
  const invest = useTradingAssetRows('invest');
  const earn = useTradingAssetRows('earn');
  const markets = useMarketsSearchAssets();

  const walletRows = useMemo(
    () =>
      wallets.rows.filter(row =>
        matchesSearchQuery(normalizedQuery, [row.code, row.name])
      ),
    [wallets.rows, normalizedQuery]
  );

  const tradingGroups: Array<TradingSearchGroup> = useMemo(() => {
    const groups: Array<TradingSearchGroup> = [
      { type: 'spot' as const, rows: spot.rows },
      { type: 'leverage' as const, rows: leverage.rows },
      { type: 'invest' as const, rows: invest.rows },
      { type: 'earn' as const, rows: earn.rows },
    ].map(group => ({
      type: group.type,
      rows: group.rows.filter(row =>
        matchesSearchQuery(normalizedQuery, [row.code, row.name])
      ),
    }));

    return groups.filter(group => group.rows.length > 0);
  }, [spot.rows, leverage.rows, invest.rows, earn.rows, normalizedQuery]);

  const marketAssets: Array<MarketAsset> = useMemo(
    () =>
      markets.assets.filter(asset =>
        matchesSearchQuery(normalizedQuery, [asset.code, asset.name])
      ),
    [markets.assets, normalizedQuery]
  );

  return {
    normalizedQuery,
    walletRows,
    walletsAccountNumber: wallets.mainAccount?.accNum,
    walletsBaseCode: wallets.baseCode,
    isWalletsLoading: wallets.isLoading,
    tradingGroups,
    tradingOpenTypes: spot.openTypes,
    tradingBaseCode: spot.baseCode,
    isTradingLoading: spot.isLoading,
    marketAssets,
    marketsBaseAsset: markets.baseAsset,
    isMarketsLoading: markets.isLoading,
  };
};

export { useGlobalSearchResults };
